// Libs
import type { DateTime } from 'luxon';

// Types
import type { TTypeDinamico } from '@/types/TTypeDinamico';

// Stores
import type { TFiltroFixoTemaStore } from '@/stores/TemaStore';

// Enums
import type ETipoDeDado from '@/shared/enums/ETipoDeDado';
import EOperadorFiltro from '@/shared/enums/EOperadoresFiltro';

// Services
import type CBaseModel from '@/services/base/CBaseModel';
import CBaseMapeamento, { type TMapeamento } from '@/services/base/CBaseMapeamento';
import type CPreferenciasFiltroFixoDashboard from '@/services/storage/preferencias/CPreferenciasFiltroFixoDashboard';

export interface IFiltroAPI<T> {
  campo: TTypeDinamico<T>;
  operador: EOperadorFiltro;
  valor: any;
}

export type TOrdenacaoFiltroAPI = 'ASC' | 'DESC';
export type TFiltroFixo<T> = CPreferenciasFiltroFixoDashboard<T, any> | null;

// Implementação da classe CFiltro
export default class CFiltro<T> {
  pagina: number = 1;
  limite: number = 10;
  totalDeRegistros: number | null = null;

  ordenacao: { campo: TTypeDinamico<T>; ordem: TOrdenacaoFiltroAPI; ordenacaoPeloCodigo?: boolean }[] = [];
  campos: Array<keyof T> = [];
  filtros: IFiltroAPI<T>[] = [];

  distinct: boolean = false;
  utilizaLimite: boolean = true;
  utilizaTotalRegistros: boolean = false;

  filtroFixo: TFiltroFixo<T> = null;

  /**
   *  Propriedade identificadora de mudança de filtro/realização da filtragem
   * - Utilizada para resetar a paginação de tabelas ServerSide
   */
  key: number = 0;

  public static toQuery(pFiltro: CFiltro<any>, pMapeamento: CBaseMapeamento<any, any>): CFiltro<any> {
    const query: CFiltro<any> = new CFiltro<any>();

    query.pagina = pFiltro.pagina;
    query.limite = pFiltro.limite;
    query.totalDeRegistros = pFiltro.totalDeRegistros;

    // Define se utilizará a opção "distinct" (Usado na consulta de valores)
    query.distinct = pFiltro.distinct;
    query.utilizaLimite = pFiltro.utilizaLimite;
    query.utilizaTotalRegistros = pFiltro.utilizaTotalRegistros;
    query.ordenacao = pFiltro.ordenacao;
    query.campos = pFiltro.campos;

    const lFiltros: IFiltroAPI<any>[] = [];

    if (pFiltro.filtroFixo?.filtros) {
      lFiltros.push(...pFiltro.filtroFixo.filtros);
    }

    if (pFiltro?.filtros) {
      lFiltros.push(...pFiltro.filtros);
    }

    // Serializa os filtros
    for (const item of lFiltros) {
      const filtro = Object.assign({}, item);
      const mapeamento = pMapeamento.getMapeamentoPorCampoClasse(String(filtro.campo)) as TMapeamento<CBaseModel, any>;

      filtro.valor = CBaseMapeamento.serializar(mapeamento, filtro.valor);

      query.filtros.push({
        campo: String(mapeamento.campoClasse),
        operador: filtro.operador,
        valor: filtro.valor,
      });
    }

    return query;
  }

  constructor(pClasseFiltro?: Partial<CFiltro<T>>) {
    if (!pClasseFiltro) return;

    Object.assign(this, JSON.parse(JSON.stringify(pClasseFiltro)));

    if (pClasseFiltro.filtros) {
      if (!Array.isArray(pClasseFiltro.filtros)) {
        pClasseFiltro.filtros = [pClasseFiltro.filtros];
      }

      pClasseFiltro.filtros.forEach((filtro) => {
        this.addFiltro(filtro.campo, filtro.operador, filtro.valor);
      });
    }

    this.filtroFixo = pClasseFiltro.filtroFixo ?? this.filtroFixo;
  }

  // Método para adicionar filtros
  addFiltro(pCampo: TTypeDinamico<T>, pOperador: EOperadorFiltro, pValor: string | DateTime | string[]): CFiltro<T> {
    if (!pCampo || !pOperador || pValor === undefined || pValor === null) {
      throw new Error(`Filtro inválido! Campo: ${String(pCampo)}, Operador: ${pOperador}, Valor: ${pValor}`);
    }

    const lValor: any = Array.isArray(pValor) ? pValor : String(pValor);

    if (this.obterFiltro(pCampo, pOperador, lValor)) return this;

    const pFiltro: IFiltroAPI<T> = {
      campo: pCampo,
      operador: pOperador,
      valor: lValor,
    };

    this.filtros.push(pFiltro);

    return this;
  }

  obterFiltro(pCampo: TTypeDinamico<T>, pOperador: EOperadorFiltro, pValor: string): IFiltroAPI<T> | null {
    let lFiltro = null;

    for (const filtro of this.filtros) {
      if (filtro.campo == pCampo && filtro.operador == pOperador && filtro.valor == pValor) {
        lFiltro = filtro;
        break;
      }
    }

    return lFiltro;
  }

  limparFiltros(): CFiltro<T> {
    this.filtros = [];
    return this;
  }

  resetarPaginacao() {
    this.pagina = 1;
    this.limite = 10;
    this.totalDeRegistros = null;
  }

  // Método para remover filtros
  removerFiltro(pCampo: keyof T, pOperador: EOperadorFiltro, pValor: any): CFiltro<T> {
    // Validação dos operadores
    CFiltro.isOperadorValido(pOperador);

    this.filtros = this.filtros.filter(
      (filtro) => filtro.campo !== pCampo || filtro.operador !== pOperador || filtro.valor != pValor,
    );

    return this;
  }

  removerFiltroPorCampo(pCampo: keyof T): CFiltro<T> {
    this.filtros = this.filtros.filter((filtro) => filtro.campo !== pCampo);
    return this;
  }

  // Método para validar os operadores
  static isOperadorValido(pOperador: EOperadorFiltro): void {
    const operadoresValidos: EOperadorFiltro[] = Object.values(EOperadorFiltro);

    if (!operadoresValidos.includes(pOperador)) {
      throw new Error(`Operador inválido: ${pOperador}`);
    }
  }

  static operadoresPorTipo(pTipoCampo: ETipoDeDado) {
    try {
      const operadoresTexto: EOperadorFiltro[] = [
        EOperadorFiltro.Igual,
        EOperadorFiltro.Diferente,
        EOperadorFiltro.Contendo,
        EOperadorFiltro.NaoContendo,
      ];
      const operadoresNumericos: EOperadorFiltro[] = [
        EOperadorFiltro.Igual,
        EOperadorFiltro.Diferente,
        EOperadorFiltro.Maior,
        EOperadorFiltro.Menor,
        EOperadorFiltro.MaiorOuIgual,
        EOperadorFiltro.MenorOuIgual,
      ];
      const operadoresBoolean: EOperadorFiltro[] = [EOperadorFiltro.Igual, EOperadorFiltro.Diferente];

      let operadoresPermitidos: EOperadorFiltro[];

      switch (pTipoCampo) {
        case 'String':
          operadoresPermitidos = operadoresTexto;
          break;
        case 'Integer':
        case 'Number':
          operadoresPermitidos = operadoresNumericos;
          break;
        case 'Boolean':
          operadoresPermitidos = operadoresBoolean;
          break;
        case 'Date':
        case 'DateTime':
          operadoresPermitidos = operadoresNumericos;
          break;
        default:
          throw new Error(`Tipo de campo não suportado: ${pTipoCampo}`);
      }

      return operadoresPermitidos;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Método criado para garantir que o filtro fixo seja da classe esperada.
   *
   * Em caso positivo, permite ao TypeScript inferir o tipo corretamente.
   * Caso contrário, lança uma exceção.
   *
   * @param filtroFixo
   * O filtro fixo a ser testado.
   *
   * @param classeFiltroFixo
   * A classe esperada para o filtro fixo.
   *
   * @remarks
   * Tipos envolvidos:
   * - `TFiltroFixo`
   *   -> Tipo definido para o filtro fixo dentro da classe `CFiltro`.
   * - `TFiltroFixoTemaStore`
   *   -> Tipo definido para o filtro fixo quando passado pela store.
   */
  static assertsClasseFiltroFixo<ClasseFiltroFixo extends CPreferenciasFiltroFixoDashboard<any, any>>(
    filtroFixo: TFiltroFixo<any> | TFiltroFixoTemaStore,
    classeFiltroFixo: new (...args: any[]) => ClasseFiltroFixo,
  ): asserts filtroFixo is ClasseFiltroFixo {
    if (filtroFixo instanceof classeFiltroFixo) return;
    throw new Error(
      `O filtro fixo do CFiltro deve ser do tipo ${classeFiltroFixo.name} mas foi recebido como ${typeof filtroFixo}`,
    );
  }
}
